import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AzureCtx = createContext(null);

export function azErr(e) {
  let d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.error && typeof d.error === "object") d = d.error;
  if (d && typeof d === "object" && d.code) return `${d.code}: ${d.message}`;
  if (d && typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join("; ");
  if (typeof d === "string") return d;
  return e?.message || "Something went wrong.";
}

export function AzureProvider({ children }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const bcRef = useRef(null);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/azure/state");
    setState(data);
    return data;
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Keep the main portal window and any popped-out lab window in sync.
  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;
    const bc = new BroadcastChannel("azure-state-sync");
    bcRef.current = bc;
    bc.onmessage = (e) => { if (e.data === "refresh") refresh(); };
    return () => { bc.close(); bcRef.current = null; };
  }, [refresh]);

  // Generic mutation: runs request, refreshes state, surfaces Azure errors as toasts.
  const run = useCallback(
    async (fn, { success } = {}) => {
      try {
        const res = await fn();
        await refresh();
        bcRef.current?.postMessage("refresh");
        if (success) toast.success(success);
        return { ok: true, res };
      } catch (e) {
        toast.error(azErr(e));
        return { ok: false, error: azErr(e) };
      }
    },
    [refresh]
  );

  const persona = state?.personas?.find((p) => p.is_current);

  return (
    <AzureCtx.Provider value={{ state, loading, refresh, run, persona }}>
      {children}
    </AzureCtx.Provider>
  );
}

export const useAzure = () => useContext(AzureCtx);
